import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";

// Searchable select that can also create a new option from the typed text.
export default function CreatableCombobox({ value, options, onChange, onCreate, placeholder, noneLabel, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = options.find(o => o.id === value);
  const q = query.trim();
  const exact = options.some(o => o.name.toLowerCase() === q.toLowerCase());

  const pick = (id) => { onChange(id); setOpen(false); setQuery(""); };
  const create = async () => {
    setBusy(true);
    try { const id = await onCreate(q); toast.success(`Created "${q}"`); pick(id); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between rounded-sm font-normal h-10" data-testid={testid}>
          <span className={`truncate ${selected ? "" : "text-slate-400"}`}>{selected?.name || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
        <Command>
          <CommandInput placeholder={onCreate ? "Search or type a new name…" : "Search…"} value={query}
            onValueChange={setQuery} data-testid={`${testid}-input`}/>
          <CommandList>
            <CommandEmpty>{onCreate ? "No match — create it below." : "No results."}</CommandEmpty>
            <CommandGroup>
              {noneLabel && (
                <CommandItem value={`__none__ ${noneLabel}`} onSelect={() => pick("")} data-testid={`${testid}-none`}>
                  <Check className={`w-4 h-4 mr-2 ${!value ? "opacity-100" : "opacity-0"}`}/>{noneLabel}
                </CommandItem>
              )}
              {options.map(o => (
                <CommandItem key={o.id} value={`${o.name} ${o.id}`} onSelect={() => pick(o.id)} data-testid={`${testid}-option-${o.id}`}>
                  <Check className={`w-4 h-4 mr-2 ${value === o.id ? "opacity-100" : "opacity-0"}`}/>{o.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {onCreate && q && !exact && (
              <CommandGroup>
                <CommandItem value={`__create__ ${q}`} onSelect={create} disabled={busy} className="text-[#E11D48]" data-testid={`${testid}-create`}>
                  <Plus className="w-4 h-4 mr-2"/>Create "{q}"
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
